import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { Ban, Check, CheckCircle2, ChevronDown, FileText, Link2, ListPlus, Paperclip, Trash2, Upload, X } from 'lucide-react';
import BlockedReasonDialog, { blockedByIdsOf, blockedByNames, type BlockedSubmit } from './BlockedReasonDialog';
import BlockedToggle from './BlockedToggle';
import ControlLink from './ControlLink';
import RichTextField from './RichTextField';
import { OwnerPicker } from './InitiativeOwners';
import { stateGroupDotClass } from './ProgressParts';
import { useOverlays } from './overlay-context';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { apiFetch, apiFetchBlob, v1Path } from '../../lib/api';
import {
  deleteAttachment,
  listWorkItemAttachments,
  uploadPendingAttachment,
  uploadWorkItemAttachment,
  type AttachmentDto,
} from '../../lib/attachments';
import { isStepDone, type LaunchStepRow } from '../../lib/launch';
import { doneStates, fileSize, linkParagraph, normalizeLink, patchTask, requirementStatusLabel, requirementStatusOptions } from '../../lib/requirement-card';
import { isRichTextEmpty } from '../../lib/rich-text';
import { cn } from '../../lib/utils';
import { type MemberDto, type StateDto, type WorkItemFull, type WorkItemRef } from '../../lib/work-types';

function changed() {
  window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function AddSubtask({
  parentTitle,
  members,
  disabled,
  onAdd,
}: {
  parentTitle: string;
  members: MemberDto[];
  disabled: boolean;
  onAdd: (title: string, ownerIds: string[]) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    const ok = await onAdd(title.trim(), ownerIds);
    setBusy(false);
    if (!ok) return;
    setTitle('');
    setOwnerIds([]);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Add a subtask to ${parentTitle}`}
          className="flex min-h-8 items-center gap-1.5 rounded-md border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted/50 disabled:cursor-default"
        >
          <ListPlus className="h-4 w-4" aria-hidden />
          Add subtask
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" aria-label={`New subtask for ${parentTitle}`}>
        <form onSubmit={(event) => void submit(event)} className="grid gap-3">
          <p className="text-sm font-medium">New subtask</p>
          <Input
            autoFocus
            className="h-8 text-sm"
            placeholder="e.g. Acme reseller agreement"
            aria-label="Subtask title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <OwnerPicker label="subtask" className="min-h-8 py-0.5 text-xs" value={ownerIds} members={members} onChange={setOwnerIds} />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={busy || !title.trim()}>
              Add subtask
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** One control for a requirement's status, in place of separate done and blocked buttons. */
function RequirementStatus({
  title,
  state,
  states,
  blocked,
  readOnly,
  onPick,
  onRemove,
}: {
  title: string;
  state: StateDto | undefined;
  states: StateDto[];
  blocked: boolean;
  readOnly: boolean;
  onPick: (choice: StateDto | 'blocked') => void;
  /** Offered when the workspace has a Cancelled state to move it to. */
  onRemove?: () => void;
}) {
  const label = blocked ? 'Blocked' : requirementStatusLabel(state);
  const face = (
    <span
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium',
        blocked
          ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200'
          : state?.group === 'completed'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
            : 'text-foreground',
      )}
    >
      {blocked ? (
        <Ban className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <span className={cn('h-2 w-2 rounded-full', stateGroupDotClass(state?.group ?? ''))} aria-hidden />
      )}
      {label}
      {readOnly ? null : <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />}
    </span>
  );
  if (readOnly) return <span aria-label={`${title} status`}>{face}</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`${title} status`} className="rounded-md">
          {face}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {requirementStatusOptions(states, state).map((option) => (
          <DropdownMenuItem key={option.id} onSelect={() => onPick(option)}>
            <span className={cn('h-2 w-2 rounded-full', stateGroupDotClass(option.group))} aria-hidden />
            <span className="flex-1">{requirementStatusLabel(option)}</span>
            {option.id === state?.id && !blocked ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
        {states.length > 0 ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem onSelect={() => onPick('blocked')}>
          <Ban className="h-3.5 w-3.5 text-red-600" aria-hidden />
          <span className="flex-1">Blocked</span>
          {blocked ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
        </DropdownMenuItem>
        {onRemove ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onRemove} className="text-red-700 focus:text-red-700 dark:text-red-300">
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              <span className="flex-1">Remove from initiative</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A business requirement as a short form: who owns it, when it is due, what was handed in
 * (files, links, notes) and a checklist for requirements with several parts. It turns orange once
 * something is handed in, red while blocked, and green when marked done; requirements are documents,
 * so they never go live.
 */
export default function RequirementCard({
  row,
  number,
  state,
  states,
  members,
  readOnly,
}: {
  row: LaunchStepRow;
  number: number;
  state: StateDto | undefined;
  states: StateDto[];
  members: MemberDto[];
  readOnly: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState<WorkItemFull | null>(null);
  const [files, setFiles] = useState<AttachmentDto[]>([]);
  const [notes, setNotes] = useState<string | null>(null);
  const [notesBase, setNotesBase] = useState<string | null>(null);
  const notesRef = useRef<string | null>(null);
  const notesFocused = useRef(false);
  const notesBaseRef = useRef<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const loadedNotes = useRef<string | null>(null);
  const done = isStepDone(state);
  const drafted = !done && (Boolean(row.has_notes) || files.length > 0);
  const blocked = !done && Boolean(row.blocked_reason);
  const { done: doneState, open: openState } = doneStates(states);
  const cancelledState = states.find((entry) => entry.group === 'cancelled');
  const checklist = (item?.children ?? []).filter((child) => child.type === 'task' && !child.archived_at);
  const checklistDone = row.rollup && row.rollup.total > 0 ? `${row.rollup.done}/${row.rollup.total}` : null;
  const blockerIds = blockedByIdsOf(row);
  const blockers = blockedByNames(blockerIds, members);
  const ownerIds = row.assignee_ids;
  const dirtyNotes =
    notes !== null &&
    notesBase !== null &&
    notes !== notesBase &&
    !(isRichTextEmpty(notes) && isRichTextEmpty(notesBase));

  const showNotes = useCallback((html: string, saved: boolean) => {
    notesRef.current = html;
    setNotes(html);
    if (saved || !notesFocused.current) {
      notesBaseRef.current = html;
      setNotesBase(html);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      setFiles(await listWorkItemAttachments(row.project_id, row.id));
    } catch {
      setFiles([]);
    }
  }, [row.project_id, row.id]);

  const loadItem = useCallback(async () => {
    const fresh = await apiFetch<WorkItemFull>(v1Path(`/projects/${row.project_id}/work-items/${row.id}`));
    setItem(fresh);
    const current = notesRef.current;
    if (current === null || current === loadedNotes.current || current === notesBaseRef.current) showNotes(fresh.description, true);
    loadedNotes.current = fresh.description;
    return fresh;
  }, [row.project_id, row.id, showNotes]);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles, row.version]);

  useEffect(() => {
    if (!open) return;
    loadItem().catch((err: unknown) => toast(errorText(err, 'Could not load this requirement')));
  }, [open, loadItem, toast, row.version]);

  async function save(fields: Record<string, unknown>, failure: string) {
    try {
      await patchTask(row.project_id, row.id, fields);
      changed();
      if (open) await loadItem();
    } catch (err) {
      toast(errorText(err, failure));
    }
  }

  function pickStatus(choice: StateDto | 'blocked') {
    if (choice === 'blocked') {
      setBlockOpen(true);
      return;
    }
    if (choice.id === state?.id && !row.blocked_reason) return;
    const fields: Record<string, unknown> = { state_id: choice.id };
    if (row.blocked_reason) fields.blocked_reason = null;
    void save(fields, 'Could not change the status');
  }

  async function removeRequirement() {
    if (!cancelledState) return;
    const ok = await confirm({
      title: 'Remove requirement',
      description: `Remove “${row.title}” from this initiative? It will no longer show here.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    await save({ state_id: cancelledState.id }, 'Could not remove the requirement');
  }

  async function saveBlocked(next: BlockedSubmit) {
    try {
      await patchTask(row.project_id, row.id, {
        blocked_reason: next.reason,
        blocked_by_ids: next.blockedByIds,
      });
      changed();
      if (open) await loadItem();
    } catch (err) {
      toast(errorText(err, 'Could not mark it blocked'));
      throw err;
    }
  }

  async function uploadFiles(list: FileList | File[]) {
    const chosen = Array.from(list);
    if (chosen.length === 0 || readOnly) return;
    setUploading(true);
    const failed: string[] = [];
    for (const file of chosen) {
      try {
        await uploadWorkItemAttachment(row.project_id, row.id, file);
      } catch (err) {
        failed.push(`${file.name}: ${errorText(err, 'upload failed')}`);
      }
    }
    setUploading(false);
    await loadFiles();
    if (failed.length) toast(failed.join(' · '));
    else toast(chosen.length === 1 ? `Added ${chosen[0].name}` : `Added ${chosen.length} files`);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files?.length) void uploadFiles(event.dataTransfer.files);
  }

  async function openFile(file: AttachmentDto) {
    try {
      const blob = await apiFetchBlob(file.download_url);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      link.rel = 'noopener';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast(errorText(err, 'Download failed'));
    }
  }

  async function removeFile(file: AttachmentDto) {
    const ok = await confirm({
      title: 'Remove file',
      description: `Remove ${file.filename} from ${row.title}?`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteAttachment(file.id);
      await loadFiles();
    } catch (err) {
      toast(errorText(err, 'Could not remove the file'));
    }
  }

  async function addLink(event: FormEvent) {
    event.preventDefault();
    const url = normalizeLink(linkUrl);
    if (!url) {
      toast('Enter a web address, like https://docs.google.com/…');
      return;
    }
    setSaving(true);
    try {
      const fresh = item ?? (await loadItem());
      const base = notes ?? fresh.description ?? '';
      const next = `${isRichTextEmpty(base) ? '' : base}${linkParagraph(url, linkLabel)}`;
      await patchTask(row.project_id, row.id, { description: next });
      showNotes(next, true);
      setLinkUrl('');
      setLinkLabel('');
      setLinkOpen(false);
      await loadItem();
      changed();
      toast('Link added');
    } catch (err) {
      toast(errorText(err, 'Could not add the link'));
    } finally {
      setSaving(false);
    }
  }

  async function saveNotes() {
    if (!dirtyNotes || notes === null) return;
    const saving = notes;
    try {
      await patchTask(row.project_id, row.id, { description: saving });
      showNotes(saving, true);
      changed();
    } catch (err) {
      toast(errorText(err, 'Could not save the notes'));
    }
  }

  async function addSubtask(title: string, ownerIds: string[]): Promise<boolean> {
    try {
      await apiFetch(v1Path(`/projects/${row.project_id}/work-items`), {
        method: 'POST',
        body: JSON.stringify({ title, parent_id: row.id, type: 'task', assignee_ids: ownerIds }),
      });
      setOpen(true);
      await loadItem();
      changed();
      toast(`Added “${title}”`);
      return true;
    } catch (err) {
      toast(errorText(err, 'Could not add the subtask'));
      return false;
    }
  }

  async function setSubtaskOwners(child: WorkItemRef, ownerIds: string[]) {
    try {
      await patchTask(child.project_id, child.id, { assignee_ids: ownerIds });
      await loadItem();
      changed();
    } catch (err) {
      toast(errorText(err, 'Could not change the owner'));
    }
  }

  async function refreshAfterBlock() {
    if (open) await loadItem();
    changed();
  }

  async function toggleChecklistItem(child: WorkItemRef) {
    const target = child.state.group === 'completed' ? openState : doneState;
    if (!target) return;
    try {
      await patchTask(child.project_id, child.id, { state_id: target.id });
      await loadItem();
      changed();
    } catch (err) {
      toast(errorText(err, 'Could not update the item'));
    }
  }

  async function removeChecklistItem(child: WorkItemRef) {
    const ok = await confirm({
      title: 'Remove item',
      description: `Remove “${child.title}” from ${row.title}?`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiFetch(v1Path(`/projects/${child.project_id}/work-items/${child.id}`), { method: 'DELETE' });
      await loadItem();
      changed();
    } catch (err) {
      toast(errorText(err, 'Could not remove the item'));
    }
  }

  const summary = [
    files.length ? `${files.length} ${files.length === 1 ? 'file' : 'files'}` : null,
    checklistDone ? `${checklistDone} subtasks` : null,
  ].filter(Boolean);

  return (
    <li
      className={cn(
        'p-3',
        done && 'bg-emerald-50/60 dark:bg-emerald-950/20',
        !done && blocked && 'bg-red-50/80 dark:bg-red-950/20',
        !done && !blocked && drafted && 'bg-orange-50/80 dark:bg-orange-950/20',
      )}
      data-status={done ? 'done' : blocked ? 'blocked' : drafted ? 'drafted' : 'empty'}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            done
              ? 'bg-emerald-500 text-white'
              : blocked
                ? 'bg-red-500 text-white'
                : drafted
                  ? 'bg-orange-400 text-white'
                  : 'bg-muted text-muted-foreground',
          )}
          aria-hidden
        >
          {done ? <CheckCircle2 className="h-4 w-4" /> : number}
        </span>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-w-[10rem] flex-1 items-center gap-2 text-left"
        >
          <span className="truncate text-sm font-medium">{row.title}</span>
          {summary.length ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <Paperclip className="h-3 w-3" aria-hidden />
              {summary.join(' · ')}
            </span>
          ) : null}
        </button>
        <RequirementStatus
          title={row.title}
          state={state}
          states={states}
          blocked={Boolean(row.blocked_reason)}
          readOnly={readOnly}
          onPick={pickStatus}
          onRemove={cancelledState ? () => void removeRequirement() : undefined}
        />
        <BlockedReasonDialog
          open={blockOpen}
          onOpenChange={setBlockOpen}
          workItemKey={row.key}
          projectId={row.project_id}
          members={members}
          initialReason={row.blocked_reason}
          initialBlockedByIds={blockerIds}
          onSubmit={saveBlocked}
        />
        {readOnly ? null : (
          <AddSubtask parentTitle={row.title} members={members} disabled={saving} onAdd={addSubtask} />
        )}
        <OwnerPicker
          label={row.title}
          className="min-h-8 py-0.5 text-xs"
          value={ownerIds}
          members={members}
          disabled={readOnly}
          onChange={(ids) => void save({ assignee_ids: ids }, 'Could not change the owner')}
        />
        <Input
          type="date"
          className="h-8 w-[9.5rem] text-xs"
          aria-label={`${row.title} due date`}
          title="Due date"
          value={row.due_date ?? ''}
          disabled={readOnly}
          onChange={(event) => void save({ due_date: event.target.value || null }, 'Could not change the date')}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          aria-label={open ? `Close ${row.title}` : `Open ${row.title}`}
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
        </Button>
      </div>

      {row.blocked_reason ? (
        <p className="mt-2 flex items-start gap-1.5 pl-10 text-xs text-red-800 dark:text-red-200" data-testid="blocked-note">
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            <span className="font-medium">Blocked:</span> {row.blocked_reason}
            {blockers ? <span className="text-red-700/80 dark:text-red-300/80"> · waiting on {blockers}</span> : null}
          </span>
        </p>
      ) : null}

      {open ? (
        <div className="mt-3 grid gap-4 pl-10">
          <div className="grid gap-2">
            <p className="text-xs font-medium text-muted-foreground">Files and links</p>
            {readOnly ? null : (
              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={cn(
                  'flex flex-wrap items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-4 text-sm text-muted-foreground',
                  dragging && 'border-primary bg-primary/5 text-foreground',
                )}
              >
                <Upload className="h-4 w-4" aria-hidden />
                <span>{uploading ? 'Uploading…' : 'Drop files here, or'}</span>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  className="sr-only"
                  aria-label={`Upload files to ${row.title}`}
                  onChange={(event) => {
                    const chosen = event.target.files;
                    if (chosen) void uploadFiles(chosen);
                    event.target.value = '';
                  }}
                />
                <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileInput.current?.click()}>
                  Choose files
                </Button>
                <span>or</span>
                <Button type="button" size="sm" variant="outline" onClick={() => setLinkOpen((value) => !value)}>
                  <Link2 className="h-4 w-4" aria-hidden />
                  Add a link
                </Button>
              </div>
            )}
            {linkOpen ? (
              <form onSubmit={(event) => void addLink(event)} className="flex flex-wrap items-center gap-2">
                <Input
                  autoFocus
                  className="h-8 min-w-[16rem] flex-1 text-sm"
                  placeholder="Paste a link, e.g. a Google Doc or Drive folder"
                  aria-label="Link address"
                  value={linkUrl}
                  onChange={(event) => setLinkUrl(event.target.value)}
                />
                <Input
                  className="h-8 w-48 text-sm"
                  placeholder="Name (optional)"
                  aria-label="Link name"
                  value={linkLabel}
                  onChange={(event) => setLinkLabel(event.target.value)}
                />
                <Button type="submit" size="sm" disabled={saving || !linkUrl.trim()}>
                  Add link
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setLinkOpen(false)}>
                  Cancel
                </Button>
              </form>
            ) : null}
            {files.length > 0 ? (
              <ul className="divide-y rounded-md border text-sm">
                {files.map((file) => (
                  <li key={file.id} className="flex items-center gap-2 px-3 py-1.5">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <button type="button" className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => void openFile(file)}>
                      {file.filename}
                    </button>
                    <span className="shrink-0 text-xs text-muted-foreground">{fileSize(file.byte_size)}</span>
                    {readOnly ? null : (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label={`Remove ${file.filename}`}
                        onClick={() => void removeFile(file)}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {checklist.length > 0 ? (
            <div className="grid gap-2">
              <p className="text-xs font-medium text-muted-foreground">Subtasks</p>
              <ul className="divide-y rounded-md border">
                {checklist.map((child) => {
                  const checked = child.state.group === 'completed';
                  return (
                    <li key={child.id} className="group flex items-center gap-2 px-3 py-1.5 text-sm">
                      <Checkbox
                        checked={checked}
                        disabled={readOnly}
                        aria-label={`${child.title} done`}
                        onCheckedChange={() => void toggleChecklistItem(child)}
                      />
                      <ControlLink
                        workItemId={child.id}
                        workItemKey={child.key}
                        projectId={child.project_id}
                        className={cn('min-w-0 flex-1 truncate text-foreground no-underline hover:underline', checked && 'text-muted-foreground line-through')}
                      >
                        {child.title}
                      </ControlLink>
                      <BlockedToggle item={child} members={members} readOnly={readOnly} onChanged={refreshAfterBlock} />
                      <OwnerPicker
                        label={child.title}
                        className="min-h-7 py-0 text-xs"
                        value={child.assignees.map((owner) => owner.user_id)}
                        members={members}
                        disabled={readOnly}
                        onChange={(ids) => void setSubtaskOwners(child, ids)}
                      />
                      {readOnly ? null : (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label={`Remove ${child.title}`}
                          onClick={() => void removeChecklistItem(child)}
                        >
                          <X className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="grid gap-2">
            <p className="text-xs font-medium text-muted-foreground">Notes</p>
            {item && notes !== null ? (
              <RichTextField
                variant="compact"
                value={notes}
                readOnly={readOnly}
                placeholder="Write the answer here, or paste text and links."
                upload={(file) => uploadPendingAttachment(file).then((uploaded) => uploaded.download_url)}
                onUploadError={toast}
                onChange={(html) => showNotes(html, false)}
                onFocus={() => {
                  notesFocused.current = true;
                }}
                onBlur={() => {
                  notesFocused.current = false;
                  void saveNotes();
                }}
              />
            ) : (
              <p className="text-xs text-muted-foreground">Loading…</p>
            )}
            {dirtyNotes ? (
              <div>
                <Button type="button" size="sm" variant="outline" onClick={() => void saveNotes()}>
                  Save notes
                </Button>
              </div>
            ) : null}
          </div>

        </div>
      ) : null}
    </li>
  );
}
